import { Service } from "../Service/Service";
import { NotificationEntry } from "./NotificationEntry";
import { Tag } from "../HTML/Tag";
import { Div } from "../HTML/Div";
import { CFlexContainer } from "../HTML/Container/CFlexContainer";
import { List } from "../HTML/Container/List/List";
import type { Container } from "../HTML/Container/Container";
import { getDataRepository, timeout, toMap, setNotification, log2, getAppService } from "../../../HA";

export class HANotification extends Service {

    public watch(data: Map<any, any>): void {
        getDataRepository().fetchData(data).then((rData: any) => {
            this.processAction(rData);
        });
    }

    public info(msg: any) {
        this.push(msg, "dlogcontainer");
    }

    public log(msg: any) {
        const m = (!(msg instanceof Map) && msg instanceof Object) ? toMap(msg) : msg;
        this.push(m, "logcontainer");
    }

    public success(msg: any) {
        this.push(msg, "dlogcontainer success");
    }

    public warning(msg: any) {
        this.push(msg, "dlogcontainer warning");
    }

    public error(msg: any) {
        this.push(msg, "elogcontainer");
    }

    public isDebug(): boolean {
        return this.get("debug") === true;
    }

    public activateDebugMode(): void {
        this.addToData("debug", true);
        log2("Debug mode activated");
    }

    public deactivateDebugMode(): void {
        this.addToData("debug", false);
    }

    public setMainContainer(container: Container): void {
        container.appendChild(this.getNotificationContainer());
        container.hide("fixed");
        this.addToData("mcontainer", container);
    }

    protected push(data: any, classNames: string): void {
        const entry = this.buildEntry(data, classNames);
        this.getMainContainer().show();
        this.getNotificationContainer().addItem(entry);
        entry.show();
    }

    protected buildEntry(data: any, classNames: string): NotificationEntry {
        const d = (data instanceof Map) ? data : toMap({m: data});
        const entry: NotificationEntry = new NotificationEntry(d);
        d.set("end", () => { this.dismissEntry(entry); });
        classNames.split(/\s+/).forEach((cls) => { if (cls) { entry.addClass(cls); } });
        return entry;
    }

    protected dismissEntry(entry: NotificationEntry): void {
        entry.hide();
        timeout(() => {
            const items = this.getNotificationContainer().getItems();
            const id = entry.getId();
            if (id && items.has(id)) { items.delete(id); }
            const el = entry.getElement();
            if (el && el.parentNode) { el.parentNode.removeChild(el); }
            if (items.size === 0) {
                this.getMainContainer().hide("fixed");
            }
        }, 350);
    }

    protected initialize(data?: any) {
        super.initialize(data);
        setNotification(this);
        this.createNotificationContainer();
    }

    public processAction(data: Map<any, any>) {
        switch (data.get("t")) {
            case "msg":
                this.info(data);
                break;
            case "bulk":
            case "steps":
                this.processBulkAction(data.get("steps"));
                break;
            case "idle":
                timeout(() => {
                    this.processAction(toMap(data.get("data")));
                }, data.get("d"));
                break;
            case "watch":
                this.watch(data);
                break;
            case "fragment":
                this.injectFragment(data);
                break;
            default:
                getAppService().processAction(data);
                break;
        }
    }

    protected injectFragment(data: Map<any, any>): void {
        const id         = data.get("id")          as string;
        const storageKey = data.get("storage-key") as string | undefined;

        if (!id) { return; }
        if (storageKey && localStorage.getItem(storageKey)) { return; }

        getDataRepository().fetchData(toMap({ id: "ha.fragment", fragmentId: id }))
            .then((response: any) => {
                const r = (response instanceof Map) ? response : toMap(response);
                if (!r.get("success")) { return; }

                const css  = r.get("css")  as string;
                const html = r.get("html") as string;
                if (!html) { return; }

                if (css) {
                    const style = new Tag({ _tn: "style" });
                    style.getElement().textContent = css;
                    new Tag({ tagName: "head" }).appendChild(style);
                }

                const fragment = new Div({ html });
                new Tag({ tagName: "body" }).appendChild(fragment);
                timeout(() => { fragment.addClass("is-visible"); }, 400);

                if (storageKey) {
                    fragment.fetchByAttribute("data-ha-dismiss").forEach((btn: Tag) => {
                        btn.onClick(() => {
                            const choice = btn.getAttribute("data-ha-dismiss") || "accepted";
                            localStorage.setItem(storageKey, choice);
                            fragment.removeClass("is-visible");
                            fragment.addClass("is-hiding");
                            timeout(() => {
                                const el = fragment.getElement();
                                if (el && el.parentNode) { el.parentNode.removeChild(el); }
                            }, 380);
                        });
                    });
                }
            });
    }

    protected processBulkAction(data: any[]) {
        for (const item of data) {
            this.processAction(toMap(item));
        }
    }

    private createNotificationContainer(): void {
        this.addToData("_notifs", new List({tag: new CFlexContainer({className: "c100"})}));
    }

    private getNotificationContainer(): List {
        return this.get("_notifs");
    }

    private getMainContainer(): Container {
        return this.get("mcontainer");
    }
}
